/**
 * Helpers para a seleção múltipla das tabelas de Recursos Operacionais.
 *
 * MerusDataTable emite `input` duas vezes por mudança (handler + watcher
 * profundo), então os consumidores precisam comparar por chave antes de
 * reagir. Estes helpers concentram essa lógica.
 */

/** Chaves (string) dos itens de uma lista, ignorando nulos. */
export function selectionKeys(list, key) {
  return (list || [])
    .map((item) => (item ? item[key] : null))
    .filter((value) => value != null)
    .map(String);
}

/** true quando duas seleções apontam para o mesmo conjunto, na mesma ordem. */
export function sameSelection(a, b, key) {
  const ka = selectionKeys(a, key);
  const kb = selectionKeys(b, key);
  return ka.length === kb.length && ka.every((value, i) => value === kb[i]);
}

/**
 * Reaponta a seleção para os objetos novos após um reload da lista,
 * descartando as linhas que deixaram de existir.
 */
export function resyncSelection(selected, source, key) {
  const byKey = new Map(
    (source || [])
      .filter((item) => item && item[key] != null)
      .map((item) => [String(item[key]), item])
  );
  return (selected || [])
    .map((item) => (item && item[key] != null ? byKey.get(String(item[key])) : null))
    .filter(Boolean);
}

/**
 * Reaponta para os objetos da página atual sem perder o marcado em outras páginas.
 * Itens da página atual usam a referência fresca; itens de outras páginas permanecem.
 */
export function mergeSelectionWithPage(selected, pageItems, key) {
  const byKey = new Map(
    (pageItems || [])
      .filter((item) => item && item[key] != null)
      .map((item) => [String(item[key]), item])
  );
  return (selected || [])
    .map((item) => {
      if (!item || item[key] == null) return null;
      return byKey.get(String(item[key])) || item;
    })
    .filter(Boolean);
}

/**
 * Remove chaves da seleção — usar após delete, nunca após troca de página.
 */
export function dropFromSelection(selected, keysToDrop, key) {
  const drop = new Set(
    (keysToDrop || []).map((k) => String(k)).filter((k) => k !== '' && k !== 'null' && k !== 'undefined')
  );
  if (!drop.size) return selected || [];
  return (selected || []).filter((item) => item && item[key] != null && !drop.has(String(item[key])));
}

/** Item da página atual do paginador de parâmetros (índice sempre clampeado). */
export function currentPageItem(list, page) {
  const items = list || [];
  if (!items.length) return null;
  const index = Math.min(Math.max(0, Number(page) || 0), items.length - 1);
  return items[index] || null;
}
